import { useEffect, useState, type ReactElement } from 'react';

/**
 * DSGVO/TDDDG-konformes Cookie-Banner.
 *
 * - Erscheint nur, solange noch keine Entscheidung getroffen wurde.
 * - "Akzeptieren" und "Nur notwendige" sind gleichwertig (gleiche Optik/Gewicht),
 *   damit Ablehnen nicht erschwert wird (sonst abmahnfaehig).
 * - Die eigentliche Consent-Logik (Google Consent Mode v2, GA nachladen)
 *   liegt in index.html als window.saldifyConsent(granted). Diese Komponente
 *   ruft sie nur auf und merkt sich, ob bereits entschieden wurde.
 * - Ueber das Event 'saldify:open-cookie-settings' (z. B. aus dem Footer)
 *   laesst sich das Banner erneut oeffnen, um die Wahl zu aendern/widerrufen.
 */

const STORAGE_KEY = 'saldify-cookie-consent';

declare global {
  interface Window {
    saldifyConsent?: (granted: boolean) => void;
  }
}

function hasDecided(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

export default function CookieConsent(): ReactElement | null {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Beim ersten Besuch (noch keine Entscheidung) Banner zeigen.
    if (!hasDecided()) setOpen(true);

    // Footer-Link "Cookie-Einstellungen" oeffnet das Banner erneut.
    const reopen = (): void => setOpen(true);
    window.addEventListener('saldify:open-cookie-settings', reopen);
    return () => window.removeEventListener('saldify:open-cookie-settings', reopen);
  }, []);

  function decide(granted: boolean): void {
    window.saldifyConsent?.(granted);
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie-Einstellungen"
      className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6"
    >
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-secondary-200 p-6">
        <h2 className="text-lg font-semibold text-secondary-900 mb-2">
          Wir respektieren Ihre Privatsphäre
        </h2>
        <p className="text-sm text-secondary-600 mb-5">
          Wir verwenden technisch notwendige Cookies, damit Saldify funktioniert.
          Zusätzlich möchten wir mit Google Analytics anonymisierte
          Statistik-Cookies setzen, um unsere Website zu verbessern. Diese werden
          nur mit Ihrer Einwilligung geladen. Ihre Wahl können Sie jederzeit über
          „Cookie-Einstellungen" im Footer ändern. Mehr dazu in unserer{' '}
          <a href="/datenschutz" className="text-primary-600 hover:underline">
            Datenschutzerklärung
          </a>
          .
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            type="button"
            onClick={() => decide(false)}
            className="order-2 sm:order-1 px-5 py-2.5 rounded-lg text-sm font-medium border border-secondary-300 text-secondary-700 hover:bg-secondary-50 transition-colors"
          >
            Nur notwendige
          </button>
          <button
            type="button"
            onClick={() => decide(true)}
            className="order-1 sm:order-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
}
